import { CONSTANTS__client } from '@/constants/CONSTANTS__client';
import { CONSTANTS_TIME__client } from '@/constants/CONSTANTS_TIME__client';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_debounced_function_async } from '@/hooks/debounce/use_debounced_function_async';
import { use_slice_grid2d } from '@/hooks/store/use_slices';
import { REMOTE__grid2d_chunk_load_cells } from '@/state/grid2d/grid2d_chunk/REMOTE__grid2d_chunk_load_cells';
import { DTO__entity_with_grid2d } from '@shared/dto/+workspace/workspace_entity/_database_entity_with_grid2d_permissions/DTO__entity_with_grid2d';
import { ENUM__workspace_entity } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity';
import { DTO_REQUEST__grid2d_chunk_load_cells } from '@shared/dto/grid2d/_requests/DTO_REQUEST__grid2d_chunk_load_cells';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { DTO__grid2d_virtual } from '@shared/dto/grid2d_virtual/DTO__grid2d_virtual';
import { useCallback } from 'react';

interface Props {
  entity: DTO__entity_with_grid2d;
  grid2d_virtual: DTO__grid2d_virtual;
  grid2d: DTO__grid2d;
}

export function WATCHER_GRID2D_CHUNK__load_cells({ entity, grid2d_virtual, grid2d }: Props) {
  const dispatch = use_dispatch();

  const { grid2d_chunk_cells_loading_in_progress } = use_slice_grid2d(['grid2d_chunk_cells_loading_in_progress']);

  const load_cells_cached = useCallback(load_cells, [grid2d, grid2d_virtual, entity, dispatch, grid2d_chunk_cells_loading_in_progress]);
  const load_cells_debounced = use_debounced_function_async(load_cells_cached, CONSTANTS_TIME__client.DEBOUNCE_GRID2D_LOAD_CELLS);

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (grid2d_virtual.total_columns === CONSTANTS__client.GRID2D_VIRTUAL_TOTAL_COLUMNS_DEFAULT) return; // grid2d_virtual and virtualizer properties are not calculated yet based on viewport size
    if (grid2d.is_stub()) return;
    if (grid2d.entity_type === ENUM__workspace_entity.ENTITY_GENERATION) return; // will be loaded by other watcher
    if (grid2d_virtual.chunk__get_all_to_load().length === 0) return;

    void load_cells_debounced();
  }, [grid2d_virtual, grid2d]);

  /*
   * METHODS
   */

  async function load_cells() {
    if (grid2d.is_stub()) return;
    if (grid2d.entity_type === ENUM__workspace_entity.ENTITY_GENERATION) return; // will be loaded by other watcher
    if (grid2d_virtual.chunk__get_all_to_load().length === 0) return;

    const chunks_to_load = grid2d_virtual.chunk__get_all_to_load();

    let request = new DTO_REQUEST__grid2d_chunk_load_cells();
    request.workspace_id = grid2d.workspace_id;
    request.grid2d_id = grid2d._id;
    request.entity_type = entity.type;
    request.entity_id = entity._id;

    for (let chunk of chunks_to_load) {
      if (grid2d_chunk_cells_loading_in_progress.has(chunk._id)) continue; //already loading
      if (chunk.are_cells_loaded_first_time) continue; //already loaded
      if (chunk.cells_amount == 0) continue; // empty chunk

      request.grid2d_chunk_ids = [...request.grid2d_chunk_ids, chunk._id];
    }

    if (request.grid2d_chunk_ids.length > 0) {
      await dispatch(REMOTE__grid2d_chunk_load_cells(request));
    }
  }

  return null;
}
